import { createAsyncThunk } from '@reduxjs/toolkit';
import type { HttpError } from '@wix/yoshi-flow-bm';
import { getAdUnit } from '@wix/ambassador-adsense-v1-adsense-adunit/http';
import type {
  AdUnit,
  GetAdUnitResponse,
} from '@wix/ambassador-adsense-v1-adsense-adunit/types';
import { type RootState } from '@app/settings-store';
import settingsParams from '@app/settingsParams';
import { selectAdUnitId } from '@app/store/settings/slices/adsense-user-slice';
import { type StoreExtraArgs } from '@app/store/settings/types';

export const fetchAdUnitByCompId = createAsyncThunk(
  'fetchAdUnitByCompId/GET',
  async (
    _,
    { extra, rejectWithValue, getState },
  ): Promise<GetAdUnitResponse> => {
    const { httpClient, wixSdk, settings } = extra as StoreExtraArgs;
    const state = getState() as RootState;

    try {
      const instanceId = wixSdk.getInstanceId();
      const origCompId = wixSdk.getOrigCompId();

      // Get reportingDimensionId from user data, fallback to settings (public data)
      const reportingDimensionIdFromUser = selectAdUnitId(state, origCompId);
      const selectedAdUnitFromSettings = settings.get(
        settingsParams.selectedAdUnit,
      );
      const usedSettingsFallback =
        !reportingDimensionIdFromUser &&
        !!selectedAdUnitFromSettings?.reportingDimensionId;
      const reportingDimensionId =
        reportingDimensionIdFromUser ||
        selectedAdUnitFromSettings?.reportingDimensionId;

      // If no connected ad unit found, return empty response
      if (!reportingDimensionId) {
        return { adUnit: undefined };
      }

      const { data } = await httpClient.request(
        getAdUnit({ instanceId, reportingDimensionId }),
      );
      if (usedSettingsFallback && !data.adUnit) {
        settings.set(settingsParams.selectedAdUnit, {} as AdUnit);
      }

      return data;
    } catch (e) {
      const error = e as HttpError;
      const message = error.response?.data?.message || error.message;
      throw rejectWithValue(message);
    }
  },
);
